import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Chip,
  Stack,
  alpha,
  Alert,
  Switch,
  Button,
  Collapse,
  useTheme,
  Typography,
  AlertTitle,
} from '@mui/material';

import Iconify from 'src/components/iconify';

const PERMISSION_GROUPS = [
  {
    res: 'UI_ACCESS_GROUP',
    icon: 'solar:sidebar-minimalistic-bold-duotone',
    color: '#00A76F',
    acts: [
      'ROLES',
      'USERS',
      'TICKETS',
      'DEPARTMENTS',
      'FORMS',
      'CALENDAR',
      'STATUSES',
      'ATTRIBUTES',
      'SHAREABLE_FORMS',
      'CLIENT_CREDENTIALS',
      'MY_GROUPS',
      'SETTINGS',
    ],
    icons: {
      ROLES: '/assets/icons/navbar/ic_roles.svg',
      USERS: '/assets/icons/navbar/ic_user.svg',
      TICKETS: '/assets/icons/navbar/ic_ticket.svg',
      DEPARTMENTS: '/assets/icons/navbar/ic_department.svg',
      FORMS: '/assets/icons/navbar/ic_form.svg',
      CALENDAR: '/assets/icons/navbar/ic_calendar.svg',
      STATUSES: '/assets/icons/navbar/ic_flow.svg',
      ATTRIBUTES: '/assets/icons/navbar/ic_tag.svg',
      SHAREABLE_FORMS: '/assets/icons/navbar/ic_shareable_forms.svg',
      CLIENT_CREDENTIALS: '/assets/icons/navbar/ic_client_credentials.svg',
      MY_GROUPS: '/assets/icons/navbar/my_groups.svg',
      SETTINGS: '/assets/icons/navbar/ic_lock.svg',
    },
  },
  {
    res: 'TICKET',
    icon: 'solar:ticket-bold-duotone',
    color: '#2065D1',

    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE', 'UPLOAD', 'SCOPE_DEPARTMENT', 'RESOLVE'],
  },
  {
    res: 'TICKET_COMMENT',
    icon: 'solar:chat-dots-bold-duotone',
    color: '#FF5630',
    acts: ['READ', 'CREATE'],
  },
  {
    res: 'TICKET_ASSIGNEES',
    icon: 'solar:users-group-two-rounded-bold-duotone',
    color: '#00B8D9',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'USER',
    icon: 'solar:users-group-rounded-bold-duotone',
    color: '#8E33FF',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'ROLES',
    icon: 'solar:shield-keyhole-bold-duotone',
    color: '#7A0C2E',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'DEPARTMENT',
    icon: 'solar:structure-bold-duotone',
    color: '#FDA92D',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'TAG',
    icon: 'solar:tag-price-bold-duotone',
    color: '#00A76F',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'FORM',
    icon: 'solar:document-add-bold-duotone',
    color: '#00B8D9',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'TICKET_STATUS',
    icon: 'solar:checklist-minimalistic-bold-duotone',
    color: '#3366FF',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'SHAREABLE_FORM',
    icon: 'solar:link-bold-duotone',
    color: '#003768',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'CSV',
    icon: 'ph:file-csv-bold',
    color: '#107C41',
    acts: ['UPLOAD', 'DOWNLOAD'],
  },
  {
    res: 'FILE',
    icon: 'solar:file-send-bold-duotone',
    color: '#1890FF',
    acts: ['UPLOAD', 'READ'],
  },
  {
    res: 'CLIENT_CREDENTIALS',
    icon: 'solar:key-square-bold-duotone',
    color: '#FFAB00',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'PERMISSIONS',
    icon: 'solar:key-bold-duotone',
    color: '#41107C',
    acts: ['UPDATE'],
  },
  {
    res: 'EMAIL_SETTINGS',
    icon: 'solar:letter-bold-duotone',
    color: '#B71D18',
    acts: ['MANAGE'],
  },
  {
    res: 'DASHBOARD',
    icon: 'solar:graph-up-bold-duotone',
    color: '#FF4842',
    acts: ['ADMIN', 'DEPT_MANAGER', 'OPERATOR', 'CUSTOMER'],
  },
];

export default function RolePermissionSelector({ selected, onChange }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [expanded, setExpanded] = useState({ DASHBOARD: false, UI_ACCESS_GROUP: true });

  const toggleExpand = (res) => setExpanded((p) => ({ ...p, [res]: !p[res] }));

  const handleToggle = (permKey) => {
    const [res, act] = permKey.includes(':') ? permKey.split(':') : [null, null];
    const isActive = selected.includes(permKey);

    let newSelected;

    if (permKey.startsWith('DASHBOARD:')) {
      const basePerms = selected.filter((p) => !p.startsWith('DASHBOARD:'));
      newSelected = [...basePerms, permKey];
    } else {
      newSelected = isActive
        ? selected.filter((p) => p !== permKey)
        : [...selected, permKey];

      // CASE A: DISABLING
      if (isActive) {
        // If disabling READ, remove all related acts for this resource
        if (act === 'READ') {
          newSelected = newSelected.filter((p) => !p.startsWith(`${res}:`));
        }
      }
      // CASE B: ENABLING
      else {
        // If enabling functional action but READ is missing, auto-enable READ
        if (act !== 'READ' && act !== 'UI_ACCESS' && res !== 'UI_ACCESS_GROUP') {
          const groupConfig = PERMISSION_GROUPS.find((g) => g.res === res);
          if (groupConfig && groupConfig.acts.includes('READ')) {
            const readKey = `${res}:READ`;
            if (!newSelected.includes(readKey)) newSelected.push(readKey);
          }
        }

        // UI_ACCESS -> READ dependency
        if (permKey.endsWith(':UI_ACCESS')) {
          const uiResource = permKey.split(':')[0];
          const readMap = {
            ROLES: 'ROLES:READ',
            USERS: 'USER:READ',
            TICKETS: 'TICKET:READ',
            DEPARTMENTS: 'DEPARTMENT:READ',
            FORMS: 'FORM:READ',
            STATUSES: 'TICKET_STATUS:READ',
            ATTRIBUTES: 'TAG:READ',
            SHAREABLE_FORMS: 'SHAREABLE_FORM:READ',
            CLIENT_CREDENTIALS: 'CLIENT_CREDENTIALS:READ',
            SETTINGS: 'EMAIL_SETTINGS:MANAGE',
          };
          const targetRead = readMap[uiResource];
          if (targetRead && !newSelected.includes(targetRead))
            newSelected.push(targetRead);
        }
      }
    }

    onChange(Array.from(new Set(newSelected)));
  };

  return (
    <Stack spacing={2.5} sx={{ width: '100%', maxWidth: '1000px', mx: 'auto', pb: 4 }}>
      <Box sx={{ px: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            color: 'text.primary',
            mb: 2,
          }}
        >
          <Iconify icon="solar:widget-5-bold-duotone" width={24} color="#00A76F" />
          {t('permissionss.editor.moduleControl')}
        </Typography>

        <Alert
          severity="info"
          icon={<Iconify icon="solar:info-circle-bold-duotone" width={24} />}
          sx={{
            borderRadius: '16px',
            bgcolor: alpha('#00B8D9', 0.05),
            border: '1px solid rgba(0, 184, 217, 0.12)',
            mb: 3,
          }}
        >
          <AlertTitle sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
            {t('permissionss.editor.bestPracticeTipTitle')}
          </AlertTitle>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {t('permissionss.editor.bestPracticeTipDesc')}
          </Typography>
        </Alert>
      </Box>

      {PERMISSION_GROUPS.map((group) => {
        const isExpanded = !!expanded[group.res];
        const isUIGroup = group.res === 'UI_ACCESS_GROUP';
        const activeActs = group.acts.filter((act) =>
          selected.includes(isUIGroup ? `${act}:UI_ACCESS` : `${group.res}:${act}`)
        );

        return (
          <Box
            key={group.res}
            sx={{
              borderRadius: '20px',
              bgcolor: 'background.paper',
              border: isExpanded
                ? `2px solid ${group.color}`
                : `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: isExpanded ? `0 12px 24px ${alpha(group.color, 0.15)}` : 'none',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 3, cursor: 'pointer' }}
              onClick={() => toggleExpand(group.res)}
            >
              <Stack direction="row" alignItems="center" spacing={3}>
                <Box
                  sx={{
                    color: activeActs.length > 0 ? group.color : '#919EAB',
                    display: 'flex',
                  }}
                >
                  <Iconify icon={group.icon} width={36} />
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: 'text.primary' }}
                    >
                      {t(`permissionss.modules.${group.res}.title`)}
                    </Typography>

                    {group.res === 'TICKET' && (
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="soft"
                          size="small"
                          color={
                            selected.includes('TICKET:SCOPE_DEPARTMENT')
                              ? 'success'
                              : 'inherit'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle('TICKET:SCOPE_DEPARTMENT');
                          }}
                          startIcon={
                            <Iconify
                              icon={
                                selected.includes('TICKET:SCOPE_DEPARTMENT')
                                  ? 'solar:globus-bold'
                                  : 'solar:user-bold'
                              }
                            />
                          }
                          sx={{
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: 800,
                            height: '24px',
                            bgcolor: selected.includes('TICKET:SCOPE_DEPARTMENT')
                              ? alpha('#00A76F', 0.1)
                              : alpha('#919EAB', 0.1),
                          }}
                        >
                          {selected.includes('TICKET:SCOPE_DEPARTMENT')
                            ? t('permissionss.editor.showingDeptTickets')
                            : t('permissionss.editor.showDeptTickets')}
                        </Button>

                        <Button
                          variant="soft"
                          size="small"
                          color={
                            selected.includes('TICKET:RESOLVE') ? 'success' : 'inherit'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle('TICKET:RESOLVE');
                          }}
                          startIcon={
                            <Iconify
                              icon={
                                selected.includes('TICKET:RESOLVE')
                                  ? 'solar:shield-check-bold'
                                  : 'solar:shield-warning-bold'
                              }
                            />
                          }
                          sx={{
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: 800,
                            height: '24px',
                            bgcolor: selected.includes('TICKET:RESOLVE')
                              ? alpha('#00A76F', 0.1)
                              : alpha('#919EAB', 0.1),
                          }}
                        >
                          {selected.includes('TICKET:RESOLVE')
                            ? t('permissionss.editor.resolvesTickets')
                            : t('permissionss.editor.cannotResolve')}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" spacing={0.8} mt={1}>
                    {activeActs.length > 0 ? (
                      activeActs.map((act) => (
                        <Chip
                          key={act}
                          label={t(`permissionss.actions.${act}.short`)}
                          size="small"
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 900,
                            height: '20px',
                            bgcolor: alpha(group.color, 0.08),
                            color: group.color,
                            border: `1px solid ${alpha(group.color, 0.1)}`,
                            textTransform: 'uppercase',
                            borderRadius: '6px',
                          }}
                        />
                      ))
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                      >
                        {t('permissionss.editor.noAccess')}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Iconify
                icon={
                  isExpanded
                    ? 'solar:alt-arrow-up-bold-duotone'
                    : 'solar:alt-arrow-down-bold-duotone'
                }
                color="text.disabled"
                width={24}
              />
            </Stack>

            <Collapse in={isExpanded}>
              <Box
                sx={{
                  px: 4,
                  pb: 4,
                  bgcolor: 'background.neutral',
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack spacing={1} mt={3}>
                  {group.acts.map((action) => {
                    const k = isUIGroup
                      ? `${action}:UI_ACCESS`
                      : `${group.res}:${action}`;
                    const isActive = selected.includes(k);
                    const isDashboardAction = group.res === 'DASHBOARD';
                    const rowIcon = isUIGroup ? group.icons[action] : null;

                    return (
                      <Stack
                        key={k}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          py: 2,
                          px: 2,
                          borderRadius: '12px',
                          transition: 'background 0.2s',
                          '&:hover': { bgcolor: alpha(group.color, 0.04) },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          {rowIcon && (
                            <Box
                              component="img"
                              src={rowIcon}
                              sx={{ width: 24, height: 24, opacity: isActive ? 1 : 0.3 }}
                            />
                          )}
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.95rem',
                                color: isActive ? 'text.primary' : 'text.disabled',
                              }}
                            >
                              {t(
                                `permissionss.modules.${
                                  isUIGroup ? action : group.res
                                }.actions.${isUIGroup ? 'UI_ACCESS' : action}.title`
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                mt: 0.3,
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                                letterSpacing: '0.05em',
                                fontWeight: 700,
                              }}
                            >
                              {t(
                                `permissionss.modules.${
                                  isUIGroup ? action : group.res
                                }.actions.${isUIGroup ? 'UI_ACCESS' : action}.desc`
                              )}
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={isActive}
                          onChange={() =>
                            isDashboardAction && isActive ? null : handleToggle(k)
                          }
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: group.color },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              bgcolor: group.color,
                            },
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}

RolePermissionSelector.propTypes = {
  selected: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};
